import type { Update } from '@ngrx/entity';
import type { Widgets } from '@common/enums';
import type { Uuid } from '@common/types';
import type { WebLegacyWidgetEntity, WebWidgetEntity } from '@web/types';

import { createActionGroup, props } from '@ngrx/store';
import { type SelectedFileExtended } from '@common/types/file.type';

export const mapWidgetActions = createActionGroup({
    source: 'Maps',
    events: {
        Editing: props<{ widgetId: Uuid; editing: boolean }>(),
    },
});

// action for payment and edu widget modal settings
export const previewPortalAttachAction = createActionGroup({
    source: 'Payment/EDU',
    events: {
        'Modal Preview Toggle': props<{ attached: boolean }>(),
    },
});

/**
 * Widget actions — the parallel twin of {@link WidgetsLegacyActions}.
 * Same `widgets` store slice, but versioned-entity semantics: settings merge into `props` / `meta`,
 * never into the legacy `data` / `styles` bags, and placement is the widget's own
 * `parentId` — a column, or a grid when `gridCellIndex` is given — so there is no
 * separate "to grid" variant of each action.
 *
 * Moves stay on the shared id-only actions (`WidgetsLegacyActions.widgetMoved`,
 * `GridsActions.moveWidgetFrom*`); their widget-slice handlers update `parentId`
 * for a non-legacy widget.
 */
export const WidgetsActions = createActionGroup({
    source: 'PageStructure/Widgets',
    events: {
        Created: props<{ widget: WebWidgetEntity; index: number; gridCellIndex?: number; skipHistory?: boolean }>(),
        /** Re-insert without a history entry — undo of a removal, redo of an add. */
        Added: props<{ widget: WebWidgetEntity; index: number; gridCellIndex?: number }>(),
        Copied: props<{ widget: WebWidgetEntity; index: number; gridCellIndex?: number; skipHistory?: boolean }>(),
        Removed: props<{
            widget: WebWidgetEntity;
            index: number;
            gridCellIndex?: number;
            skipHistory?: boolean;
            skipUnselectEntity?: boolean;
        }>(),
        'Update properties': props<{ update: Update<WebWidgetEntity> }>(),
    },
});

const WidgetsLegacyActions = createActionGroup({
    source: 'PageStructure/Widgets Legacy',
    events: {
        init: props<{ widgets: WebLegacyWidgetEntity[] }>(),
        'Add widget': props<{ widget: WebLegacyWidgetEntity; index: number }>(),
        'Add widget to grid': props<{ widget: WebLegacyWidgetEntity; gridCellIndex: number; index: number }>(),
        'Add many widgets': props<{ widgets: WebLegacyWidgetEntity[] }>(),
        'Add many widgets to grid': props<{ widgets: WebLegacyWidgetEntity[] }>(),
        'Create widget': props<{ columnId: Uuid; index: number; widget: Widgets; skipHistory?: boolean; meta?: any }>(),
        'Create widget to grid': props<{
            columnId: Uuid;
            gridId: Uuid;
            gridCellIndex: number;
            index: number;
            widget: Widgets;
        }>(),
        'Widget created': props<{ widget: WebLegacyWidgetEntity; index: number; skipHistory?: boolean }>(),
        'Widget created to grid': props<{
            widget: WebLegacyWidgetEntity;
            gridCellIndex: number;
            index: number;
            skipHistory?: boolean;
        }>(),
        'Copy widget': props<{ widget: WebLegacyWidgetEntity; index: number }>(),
        'Copy zero widget': props<{ widget: WebLegacyWidgetEntity; index: number }>(),
        'Copy widget to grid': props<{
            widget: WebLegacyWidgetEntity;
            index: number;
            gridCellIndex: number;
            skipHistory?: boolean;
        }>(),
        'Copy many widgets': props<{ widgets: WebLegacyWidgetEntity[] }>(),
        'Remove widget': props<{
            widget: WebLegacyWidgetEntity;
            index: number;
            skipHistory?: boolean;
            skipUnselectEntity?: boolean;
        }>(),
        'Remove widget from grid': props<{
            widget: WebLegacyWidgetEntity;
            index: number;
            gridCellIndex: number;
            skipHistory?: boolean;
        }>(),
        'Remove many widgets': props<{ widgetsIds: string[] }>(),
        'Update widget properties': props<{ update: Update<WebLegacyWidgetEntity> }>(),
        'Sync widget properties': props<{ update: Update<WebLegacyWidgetEntity> }>(),
        'Update zero widget properties': props<{ update: Update<WebLegacyWidgetEntity> }>(),
        'Update many zero widgets properties': props<{ updates: Update<WebLegacyWidgetEntity>[] }>(),
        'Update widget data': props<{ update: Update<WebLegacyWidgetEntity> }>(),
        'Update widget display id': props<{ id: Uuid; displayId: string }>(),
        'Update gallery image data': props<{ id: Uuid; image: SelectedFileExtended; index: number }>(),
        'Update product image data': props<{ id: Uuid; image: SelectedFileExtended }>(),
        'Update widget styles': props<{ update: Update<WebLegacyWidgetEntity> }>(),
        'Widget moved': props<{
            widgetId: Uuid;
            prevColumnId: Uuid;
            currentColumnId: Uuid;
            previousIndex: number;
            currentIndex: number;
            skipHistory?: boolean;
        }>(),
    },
});

export default WidgetsLegacyActions;
